import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../services/api/auth';
import { isEmail, required } from '../utils/validators';

type ForgotPasswordFieldErrors = {
  email: string[];
};

export const useForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ForgotPasswordFieldErrors>({ email: [] });
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const validate = () => {
    const nextFieldErrors: ForgotPasswordFieldErrors = { email: [] };
    const nextFormErrors: string[] = [];

    const requiredError = required(email, 'auth.forgot_password.validation.required_email');
    if (requiredError) {
      nextFieldErrors.email.push(requiredError);
      nextFormErrors.push(requiredError);
    } else {
      const emailError = isEmail(email, 'auth.forgot_password.validation.invalid_email');
      if (emailError) {
        nextFieldErrors.email.push(emailError);
        nextFormErrors.push(emailError);
      }
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);
    setSubmitted(false);

    return nextFieldErrors;
  };

  const mutation = useMutation({
    mutationFn: () => authApi.forgotPassword(email),
    onSuccess: () => {
      setSubmitted(true);
      setFieldErrors({ email: [] });
      setFormErrors([]);
    },
    onError: () => {
      setSubmitted(false);
      setFormErrors(['auth.forgot_password.error']);
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextFieldErrors = validate();

    if (nextFieldErrors.email.length > 0) {
      return;
    }

    mutation.mutate();
  };

  return { email, setEmail, fieldErrors, formErrors, validate, handleSubmit, loading: mutation.isPending, submitted };
};
